// src/components/hooks/useUploadEmpresaFotos.ts

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { FotoEmpresa } from '@/components/hooks/useEmpresa';

interface UseUploadEmpresaFotosProps {
  id_empresa: string;
  id_usuario: string;
  getFotos: () => FotoEmpresa[];
  updateEmpresaLocal: (patch: any) => void;
}

export const useUploadEmpresaFotos = ({
  id_empresa,
  id_usuario,
  getFotos,
  updateEmpresaLocal,
}: UseUploadEmpresaFotosProps) => {
  const [loadingFotos, setLoadingFotos] = useState(false);

  // ======================================================
  // SUBIR FOTOS
  // autoFeature: si true, marca la primera foto subida como destacada
  // en el mismo updateEmpresaLocal, evitando race conditions de estado.
  // ======================================================
  const uploadFotos = useCallback(
    async (
      files: FileList | null,
      autoFeature = false
    ): Promise<FotoEmpresa[]> => {
      if (!files) return [];

      const fotosActuales = getFotos();

      if (fotosActuales.length >= 10) {
        toast.error('Máximo 10 fotos permitidas.');
        return [];
      }

      setLoadingFotos(true);

      const disponibles = 10 - fotosActuales.length;
      const filesToUpload = Array.from(files).slice(0, disponibles);

      const nuevasFotos: FotoEmpresa[] = [];

      for (const file of filesToUpload) {
        const { data: inserted, error: insertError } = await supabase
          .from('empresa_fotos')
          .insert({ id_empresa, usuario_creacion: id_usuario })
          .select('id_foto')
          .single();

        if (insertError || !inserted) {
          Sentry.captureException(insertError, { extra: { id_empresa } });
          toast.error('Error al crear registro de foto.');
          continue;
        }

        const id_foto = inserted.id_foto;
        const extension = file.name.split('.').pop();
        const path = `empresa_fotos/${id_empresa}/${id_foto}.${extension}`;

        const { error: uploadError } = await supabase.storage
          .from('bookoll')
          .upload(path, file, {
            cacheControl: '0',
            upsert: true,
            contentType: file.type,
          });

        if (uploadError) {
          Sentry.captureException(uploadError, {
            extra: { id_empresa, id_foto, path },
          });
          await supabase.from('empresa_fotos').delete().eq('id_foto', id_foto);
          toast.error('Error al subir foto.');
          continue;
        }

        const publicUrl = supabase.storage.from('bookoll').getPublicUrl(path)
          .data.publicUrl;

        const { error: updateError } = await supabase
          .from('empresa_fotos')
          .update({ url_foto: publicUrl, usuario_edicion: id_usuario })
          .eq('id_foto', id_foto);

        if (updateError) {
          Sentry.captureException(updateError, {
            extra: { id_empresa, id_foto },
          });
        }

        nuevasFotos.push({ id_foto, url_foto: publicUrl, destacado: false });
      }

      if (nuevasFotos.length > 0) {
        // Si autoFeature, marcar la primera foto como destacada en BD y en estado
        // en un único updateEmpresaLocal para evitar race conditions.
        if (autoFeature) {
          const primeraId = nuevasFotos[0].id_foto;

          await supabase
            .from('empresa_fotos')
            .update({ destacado: false })
            .eq('id_empresa', id_empresa);

          await supabase
            .from('empresa_fotos')
            .update({ destacado: true, usuario_edicion: id_usuario })
            .eq('id_foto', primeraId);

          nuevasFotos[0] = { ...nuevasFotos[0], destacado: true };
        }

        updateEmpresaLocal({ fotos: [...getFotos(), ...nuevasFotos] });
        toast.success('Fotos subidas correctamente.');
      }

      setLoadingFotos(false);
      return nuevasFotos;
    },
    [id_empresa, id_usuario, getFotos, updateEmpresaLocal]
  );

  // ======================================================
  // ELIMINAR FOTO
  // ======================================================
  const deleteFoto = useCallback(
    async (foto: FotoEmpresa) => {
      try {
        const fotosActuales = getFotos();

        const ext = foto.url_foto.split('.').pop()?.split('?')[0];
        const path = `empresa_fotos/${id_empresa}/${foto.id_foto}.${ext}`;

        await supabase.storage.from('bookoll').remove([path]);
        await supabase
          .from('empresa_fotos')
          .delete()
          .eq('id_foto', foto.id_foto);

        updateEmpresaLocal({
          fotos: fotosActuales.filter((f) => f.id_foto !== foto.id_foto),
        });

        toast.success('Foto eliminada.');
      } catch (error) {
        Sentry.captureException(error, {
          extra: { id_empresa, id_foto: foto.id_foto },
        });
        toast.error('No se pudo eliminar la foto.');
      }
    },
    [id_empresa, getFotos, updateEmpresaLocal]
  );

  // ======================================================
  // MARCAR FOTO COMO DESTACADA
  // ======================================================
  const setFotoDestacada = useCallback(
    async (id_foto: string) => {
      try {
        const fotosActuales = getFotos();

        const { error: clearError } = await supabase
          .from('empresa_fotos')
          .update({ destacado: false })
          .eq('id_empresa', id_empresa);

        if (clearError) throw clearError;

        const { error } = await supabase
          .from('empresa_fotos')
          .update({ destacado: true, usuario_edicion: id_usuario })
          .eq('id_foto', id_foto);

        if (error) throw error;

        updateEmpresaLocal({
          fotos: fotosActuales.map((f) => ({
            ...f,
            destacado: f.id_foto === id_foto,
          })),
        });

        toast.success('Imagen principal actualizada.');
      } catch (error) {
        Sentry.captureException(error, { extra: { id_empresa, id_foto } });
        toast.error('No se pudo marcar como imagen principal.');
      }
    },
    [id_empresa, id_usuario, getFotos, updateEmpresaLocal]
  );

  // ======================================================
  // QUITAR DESTACADO
  // ======================================================
  const quitarFotoDestacada = useCallback(
    async (id_foto: string) => {
      try {
        const fotosActuales = getFotos();

        const { error } = await supabase
          .from('empresa_fotos')
          .update({ destacado: false, usuario_edicion: id_usuario })
          .eq('id_foto', id_foto);

        if (error) throw error;

        updateEmpresaLocal({
          fotos: fotosActuales.map((f) => ({
            ...f,
            destacado: f.id_foto === id_foto ? false : f.destacado,
          })),
        });

        toast.success('Imagen principal eliminada.');
      } catch (error) {
        Sentry.captureException(error, { extra: { id_empresa, id_foto } });
        toast.error('No se pudo quitar la imagen principal.');
      }
    },
    [id_empresa, id_usuario, getFotos, updateEmpresaLocal]
  );

  return {
    loadingFotos,
    uploadFotos,
    deleteFoto,
    setFotoDestacada,
    quitarFotoDestacada,
  };
};
